/**
 * Projekt-Detail im Overlay: Klick auf [data-project-link] lädt die
 * Detailseite per fetch und zeigt deren [data-project-detail] im
 * Overlay an — die Seite bleibt ein One-Pager. Ohne JS (oder wenn der
 * Abruf scheitert) greift der normale Link auf die Detailseite.
 *
 * Klicks werden delegiert, damit auch per Load-More nachgeladene
 * Teaser funktionieren.
 *
 * URL-Handling: das offene Projekt steht als #slug in der Adresszeile,
 * damit es teil- und verlinkbar ist. Beim Laden mit #slug springt die
 * Seite zum passenden Teaser und öffnet das Overlay; beim Schliessen
 * verschwindet der Slug wieder. Vor- und Zurück-Taste schalten das
 * Overlay entsprechend um.
 *
 * Kapitel-Anker ([data-navi-anchor]) gehören weiterhin der Navigation
 * und werden hier nicht angefasst.
 */

import { track } from './tracking'

const overlay = document.querySelector('[data-overlay="project"]')
const content = overlay?.querySelector('[data-project-overlay-content]')
const closeButton = overlay?.querySelector('[data-overlay-close]')

const homeUrl = window.php_vars?.homeUrl
const projectBaseUrl = window.php_vars?.projectBaseUrl

/** Slug des gerade offenen Projekts, sonst null */
let currentSlug = null
/** Zeitpunkt des Öffnens, für die Verweildauer im Close-Event */
let openedAt = 0
/** Beim Deeplink gemerkt: nach dem Schliessen zu diesem Teaser scrollen */
let restoreSlug = null
/** Auslöser des Overlays — dorthin kehrt der Fokus beim Schliessen zurück */
let returnFocusTo = null

const teaserFor = (slug) => document.querySelector(`[data-project-link][data-project-slug="${CSS.escape(slug)}"]`)

/** Notnagel, falls ein Link ohne data-project-slug im Markup landet */
const slugFromUrl = (url) => {
  try {
    return new URL(url, window.location.href).pathname.replace(/\/+$/, '').split('/').pop()
  } catch {
    return null
  }
}

const urlFor = (slug) =>
  teaserFor(slug)?.href ?? (projectBaseUrl ? `${projectBaseUrl}${encodeURIComponent(slug)}/` : null)

// behavior: 'instant' schlägt das globale scroll-behavior: smooth — sonst
// animiert der Sprung über die halbe Seite, statt einfach dort zu sein.
const scrollToTeaser = (slug) => teaserFor(slug)?.scrollIntoView({ block: 'center', behavior: 'instant' })

/**
 * Dieselbe Seite als reines Fragment — library/project-overlay.php liefert bei
 * ?overlay=1 nur das <article data-project-detail> statt des kompletten
 * One-Pagers, den single.php sonst als Unterbau mitrendert.
 */
const fragmentUrl = (url) => `${url}${url.includes('?') ? '&' : '?'}overlay=1`

const setLoading = (loading) => overlay?.classList.toggle('is-loading', loading)

const isLocked = () => document.body.classList.contains('some-overlay-open')

/**
 * Tracking-Parameter aus den data-project-* Attributen des Detail-Markups.
 */
const metaFrom = (element) => {
  const { projectName, projectCountry, projectTag, projectNgo } = element?.dataset ?? {}

  return {
    project_slug: currentSlug,
    project_name: projectName,
    project_country: projectCountry,
    project_tag: projectTag,
    ngo_name: projectNgo,
  }
}

/**
 * Modal heisst: alles ausser dem Overlay ist stillgelegt. Das CSS setzt am
 * Hintergrund nur pointer-events: none — das hält die Maus auf, nicht aber die
 * Tab-Taste und den Screenreader. inert nimmt die Geschwister des Overlays aus
 * beidem heraus, sonst wandert der Fokus durch den unsichtbaren One-Pager.
 *
 * Über die Geschwister statt über #body-wrapper: Header und Mobile-Navi liegen
 * ausserhalb davon (header.php) und blieben sonst erreichbar.
 */
const setBackgroundInert = (inert) => {
  const siblings = overlay?.parentElement?.children

  if (!siblings) return

  for (const element of siblings) {
    if (element !== overlay) element.toggleAttribute('inert', inert)
  }
}

/**
 * Der Dialog-Titel kommt erst mit dem nachgeladenen Detail. Bis dahin — und
 * wenn der Abruf scheitert — bleibt das aria-label aus footer.php stehen.
 */
const labelOverlay = () => {
  const heading = content?.querySelector('[data-project-detail] h1')

  if (heading?.id) overlay?.setAttribute('aria-labelledby', heading.id)
  else overlay?.removeAttribute('aria-labelledby')
}

// preventScroll: das Scrollen gehört scrollToTeaser() und dem Overlay selbst,
// der Fokus soll es nicht zusätzlich verschieben.
const focusDialog = () => (closeButton ?? overlay)?.focus({ preventScroll: true })

const showOverlay = () => {
  setBackgroundInert(true)
  document.body.classList.add('overlay-project-opening')
  setTimeout(() => {
    document.body.classList.add('overlay-project-open', 'some-overlay-open')
    focusDialog()
  }, 10)
}

const hideOverlay = () => {
  document.body.classList.remove('overlay-project-open')
  setLoading(false)
  overlay?.removeAttribute('aria-labelledby')

  // Reihenfolge zählt: ein inerter Hintergrund nimmt keinen Fokus an
  setBackgroundInert(false)
  returnFocusTo?.focus({ preventScroll: true })
  returnFocusTo = null

  setTimeout(() => {
    document.body.classList.remove('overlay-project-opening', 'some-overlay-open')
    if (content) content.innerHTML = ''

    // Erst jetzt ist der Scroll wieder frei (body.some-overlay-open sperrt ihn)
    if (restoreSlug) {
      scrollToTeaser(restoreSlug)
      restoreSlug = null
    }
  }, 300)
}

/**
 * Ein echter Slug kommt aus sanitize_title(): Buchstaben, Ziffern, Bindestrich.
 * Newsletter- und Werbe-Tracker hängen ihren Kontext dagegen gerne als Fragment
 * an («#msdynmkt_trackingcontext=<guid>», «#gclid=…»). Ohne diese Prüfung wird
 * so ein Fragment als Slug behandelt, urlFor() baut daraus
 * «/projekte/msdynmkt_trackingcontext%3D<guid>/» und der Besucher landet auf
 * der 404-Seite.
 */
const SLUG_PATTERN = /^[\p{Letter}\p{Number}_-]+$/u

/** decodeURIComponent wirft bei kaputten Prozent-Sequenzen («#100%») */
const decodeHash = (hash) => {
  try {
    return decodeURIComponent(hash)
  } catch {
    return hash
  }
}

/**
 * Slug aus der Adresszeile — Kapitel-Anker und Tracker-Fragmente werden ignoriert.
 */
const hashSlug = () => {
  const slug = decodeHash(window.location.hash.slice(1))

  if (!SLUG_PATTERN.test(slug)) return null
  if (document.getElementById(slug)?.hasAttribute('data-navi-anchor')) return null

  return slug
}

const setHash = (slug, replace = false) => {
  const url = slug ? `#${encodeURIComponent(slug)}` : window.location.pathname + window.location.search

  history[replace ? 'replaceState' : 'pushState'](null, '', url)
}

/**
 * Aufgeben ohne Umleitung: Overlay zu, Slug aus der Adresszeile, der Besucher
 * bleibt auf dem One-Pager. Kein project_close-Event — es wurde nie ein
 * Projekt gezeigt.
 */
const abandonProject = () => {
  currentSlug = null
  restoreSlug = null
  hideOverlay()
  setHash(null, true)
}

const openProject = async (slug, { url, source = 'list', push = true, scroll = false, trigger = null } = {}) => {
  const target = url ?? urlFor(slug)
  if (!content || !target) return

  // Ohne übergebene URL ist target aus dem Slug zusammengesetzt und damit nur
  // geraten — dorthin wird nie hart navigiert.
  const guessed = !url

  currentSlug = slug
  openedAt = Date.now()
  // Ohne Auslöser (Deeplink, Vor/Zurück) übernimmt der Teaser die Rückkehr
  returnFocusTo = trigger ?? teaserFor(slug)

  if (scroll) {
    restoreSlug = slug
    // Solange das Overlay noch nicht offen ist, lässt sich frei scrollen
    if (!isLocked()) scrollToTeaser(slug)
  }

  content.innerHTML = ''
  setLoading(true)
  showOverlay()
  setHash(slug, !push)

  try {
    const response = await fetch(fragmentUrl(target))

    // Kein Projekt unter diesem Slug. Der Sprung auf target würde nur die
    // 404-Seite in die Adresszeile schreiben — die Detailseite ist ja
    // dieselbe URL ohne ?overlay=1.
    if (!response.ok) {
      if (currentSlug === slug) abandonProject()

      return
    }

    const html = await response.text()
    const detail = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-project-detail]')

    if (!detail) throw new Error('kein Projekt-Detail gefunden')

    // Inzwischen geschlossen oder weitergeklickt: Antwort verwerfen
    if (currentSlug !== slug) return

    // Der DOMParser liefert Knoten aus einem fremden Dokument; für die löst
    // loading="lazy" nach dem Einhängen nicht zuverlässig aus und das Bild
    // bliebe leer. Im Overlay ist ohnehin alles sofort sichtbar.
    detail.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'))

    content.appendChild(detail)
    labelOverlay()
    track('view_project', { ...metaFrom(detail), project_source: source })
  } catch {
    if (currentSlug !== slug) return

    // Netzwerk- oder Parse-Fehler: auf die vollständige Detailseite, nicht auf
    // das Fragment. Nur bei einer URL aus dem Markup — eine geratene führt ins
    // Nichts.
    if (guessed) abandonProject()
    else window.location.href = target
  } finally {
    if (currentSlug === slug) setLoading(false)
  }
}

const closeProject = ({ updateHash = true } = {}) => {
  if (!currentSlug) return

  track('project_close', {
    ...metaFrom(content?.querySelector('[data-project-detail]')),
    engagement_seconds: Math.round((Date.now() - openedAt) / 1000),
  })

  currentSlug = null
  hideOverlay()

  if (updateHash) setHash(null)
}

/**
 * Vor/Zurück im Browser: Overlay an den Zustand der URL angleichen.
 */
const syncFromHash = () => {
  const slug = hashSlug()

  if (slug === currentSlug) return

  if (slug) openProject(slug, { source: 'history', push: false, scroll: true })
  else closeProject({ updateHash: false })
}

if (overlay && content) {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-project-link]')
    if (!link) return

    event.preventDefault()

    openProject(link.dataset.projectSlug ?? slugFromUrl(link.href), {
      url: link.href,
      source: link.dataset.projectSource,
      trigger: link,
    })
  })

  overlay
    .querySelectorAll('[data-overlay-close]')
    .forEach((button) => button.addEventListener('click', () => closeProject()))

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && currentSlug) closeProject()
  })

  window.addEventListener('hashchange', syncFromHash)

  const preloaded = content.querySelector('[data-project-detail]')

  if (preloaded) {
    // Direkt aufgerufenes Projekt: Detail liegt schon im Overlay (single.php).
    // Die URL wird auf den One-Pager mit #slug umgeschrieben, damit Schliessen,
    // Verlauf und Teilen sich verhalten wie bei einem Klick auf der Startseite.
    currentSlug = preloaded.dataset.projectSlug ?? null
    openedAt = Date.now()

    // Der Server hat das Overlay per body_class schon aufgeklappt
    // (library/theme-helpers.php) — showOverlay() läuft in diesem Zweig nie,
    // Hintergrund und Fokus müssen darum hier nachgezogen werden.
    setBackgroundInert(true)
    labelOverlay()
    focusDialog()

    if (currentSlug) {
      restoreSlug = currentSlug
      returnFocusTo = teaserFor(currentSlug)
      if (homeUrl) history.replaceState(null, '', `${homeUrl}#${encodeURIComponent(currentSlug)}`)
    }

    track('view_project', { ...metaFrom(preloaded), project_source: 'deeplink' })
  } else {
    const slug = hashSlug()

    if (slug) openProject(slug, { source: 'deeplink', push: false, scroll: true })
  }
}
